import type { TrainingFocus } from "@Shared/schemas/progression/progressionSchemas";

type Metric = TrainingFocus["metric"] | null;
type Loading = { status: "loading" | "minimum"; startedAt: number } | null;

export type TrainingFocusTransitionState = { loading: Loading } & (
  | { status: "idle"; displayedMetric: Metric | undefined }
  | { status: "waiting"; displayedMetric: Metric; targetMetric: Metric }
  | { status: "transitioning"; displayedMetric: Metric; previousMetric: Metric; entered: boolean }
);

type Action =
  | { type: "sync"; metric: Metric; loading: boolean; hasLoadedTrainingFocus: boolean; now: number }
  | { type: "prepare" }
  | { type: "loadingElapsed"; startedAt: number }
  | { type: "enter" }
  | { type: "complete" };

export const initialTrainingFocusTransitionState: TrainingFocusTransitionState = {
  status: "idle",
  displayedMetric: undefined,
  loading: null,
};

const startTransitionIfReady = (state: TrainingFocusTransitionState): TrainingFocusTransitionState => {
  if (state.status !== "waiting" || state.loading || state.targetMetric === state.displayedMetric) {
    return state;
  }

  return {
    status: "transitioning",
    loading: null,
    displayedMetric: state.targetMetric,
    previousMetric: state.displayedMetric,
    entered: false,
  };
};

export const trainingFocusTransitionReducer = (state: TrainingFocusTransitionState, action: Action): TrainingFocusTransitionState => {
  switch (action.type) {
    case "sync": {
      const loading: Loading = action.loading
        ? state.loading?.status === "loading"
          ? state.loading
          : { status: "loading", startedAt: action.now }
        : state.loading
          ? { ...state.loading, status: "minimum" }
          : null;
      const next = { ...state, loading };
      if (!action.hasLoadedTrainingFocus) {
        return next;
      }
      if (next.status === "waiting") {
        return startTransitionIfReady({ ...next, targetMetric: action.metric });
      }
      // Initial and unsolicited metric updates do not animate.
      return { ...next, displayedMetric: action.metric };
    }
    case "prepare":
      return state.status === "idle" && state.displayedMetric !== undefined
        ? { ...state, status: "waiting", displayedMetric: state.displayedMetric, targetMetric: state.displayedMetric }
        : state;
    case "loadingElapsed":
      // Ignore callbacks belonging to a loading cycle that has since restarted.
      return state.loading?.status === "minimum" && state.loading.startedAt === action.startedAt
        ? startTransitionIfReady({ ...state, loading: null })
        : state;
    case "enter":
      return state.status === "transitioning" ? { ...state, entered: true } : state;
    case "complete":
      return state.status === "transitioning" ? { status: "idle", displayedMetric: state.displayedMetric, loading: state.loading } : state;
  }
};
